import * as React from 'react';
import classnames from 'classnames';
import { CoreCheckbox } from '../internal/WixUiCoreComponents/components/CoreCheckbox';
import { CheckXBold, CheckXMedium, CheckXSmall, IndeterminateXSmall, } from '@wix/wix-ui-icons-common/on-stage/system';
import { CHECKBOX_DATA_HOOKS, CHEKCBOX_DATA_KEYS } from './dataHooks';
import { st, classes } from './Checkbox.st.css';
import { IconTooltip } from '../IconTooltip';
import { Error } from '@wix/wix-ui-icons-common/on-stage';
import { TooltipSkin } from '../Tooltip/types';
import { ErrorMessageWrapper } from '../internal/ErrorMessageWrapper';
import { CheckboxVerticalAlignment, CheckboxTheme, CheckmarkStyle, } from './types';
import { getAriaDescribedBy } from '../../common/a11y';
import uniqueId from '../../common/uniqueId';
import { TPAComponentsContext, } from '../TPAComponentsConfig';
import { Asterisk } from '../internal/Asterisk/Asterisk';
/** An implementation of Checkbox for TPAs */
export class Checkbox extends React.Component {
    constructor(props) {
        super(props);
        this.inputRef = React.createRef();
        this._rootRef = React.createRef();
        this._errorIconRef = React.createRef();
        this.state = {
            focused: false,
            isErrorTooltipShown: false,
            errorIconClicked: false,
            isWithinRoot: false,
        };
        this.focus = () => {
            if (this.inputRef.current) {
                this.inputRef.current.focus();
                this.setState({ focused: true });
            }
        };
        this.showErrorTooltip = () => {
            this._toggleTooltip(true);
        };
        this.hideErrorTooltip = () => {
            this._toggleTooltip(false);
        };
        this._renderIcon = (isFocused) => {
            const { checked, indeterminate } = this.props;
            const CheckmarkIcon = this.getIcon();
            return (React.createElement("span", { className: classnames(classes.icon, {
                    [classes.focused]: isFocused,
                }), "data-hook": CHECKBOX_DATA_HOOKS.IconWrapper }, checked ? (React.createElement(CheckmarkIcon, null)) : indeterminate ? (React.createElement(IndeterminateXSmall, null)) : ('')));
        };
        this._onRootFocus = () => {
            const { isWithinRoot, errorIconClicked } = this.state;
            if (!isWithinRoot && !errorIconClicked) {
                this.setState({ isWithinRoot: true });
                this._toggleTooltip(true);
            }
        };
        this._onRootKeyDown = (e) => {
            const { id: activeElementId } = document.activeElement || {};
            if (document.activeElement &&
                ((activeElementId === this._errorIconId && this._wasTabClicked(e)) ||
                    (activeElementId === this._checkboxId && this._wasShiftTabClicked(e)))) {
                // The ErrorIcon / Checkbox input are at the edge of the Component - Tab click / Shift Tab Click exits the Component accordingly
                this._toggleTooltip(false);
                this.setState({ isWithinRoot: false });
            }
        };
        this._wasShiftTabClicked = (e) => {
            return e.shiftKey && e.key === 'Tab';
        };
        this._wasTabClicked = (e) => {
            return !e.shiftKey && e.key === 'Tab';
        };
        this._onFocus = () => {
            this.setState({ focused: true });
        };
        this._onBlur = () => {
            this.setState({ focused: false });
        };
        this._toggleTooltip = (showTooltip) => {
            this.setState({ isErrorTooltipShown: showTooltip });
        };
        this._onErrorIconMouseDown = () => {
            this.setState({ errorIconClicked: true });
        };
        this._onErrorIconClick = () => {
            const { isErrorTooltipShown } = this.state;
            this._toggleTooltip(!isErrorTooltipShown);
            this.setState({ errorIconClicked: false });
        };
        this._onClickOutside = () => {
            if (this._rootRef.current &&
                !this._rootRef.current.contains(document.activeElement)) {
                this._toggleTooltip(false);
                this.setState({ isWithinRoot: false });
            }
        };
        this._getErrorSuffix = () => {
            const { isErrorTooltipShown } = this.state;
            const { errorMessage } = this.props;
            return (React.createElement("div", { className: classes.iconToolTip },
                React.createElement(IconTooltip, { "data-hook": CHECKBOX_DATA_HOOKS.ErrorIcon, message: errorMessage, icon: React.createElement(Error, { className: classes.errorIcon }), tooltipId: this._errorMessageId, tooltipSkin: TooltipSkin.Error, showTooltip: isErrorTooltipShown, onMouseDown: this._onErrorIconMouseDown, onClick: this._onErrorIconClick, onClickOutside: this._onClickOutside, contentRef: this._errorIconRef, id: this._errorIconId })));
        };
        this.getIcon = () => {
            const { checkmarkStyle } = this.props;
            if (checkmarkStyle === CheckmarkStyle.Bold) {
                return CheckXBold;
            }
            if (checkmarkStyle === CheckmarkStyle.Medium) {
                return CheckXMedium;
            }
            return CheckXSmall;
        };
        this._errorMessageId = uniqueId('error-message');
        this._errorIconId = uniqueId('error-icon');
        this._checkboxId = uniqueId('checkbox');
    }
    getDataAttributes() {
        const { disabled, error, indeterminate, checked } = this.props;
        const { focused } = this.state;
        return {
            [CHEKCBOX_DATA_KEYS.Disabled]: disabled,
            [CHEKCBOX_DATA_KEYS.Error]: error,
            [CHEKCBOX_DATA_KEYS.Indeterminate]: indeterminate,
            [CHEKCBOX_DATA_KEYS.Checked]: checked,
            [CHEKCBOX_DATA_KEYS.Focused]: focused,
        };
    }
    render() {
        const { theme, checked, disabled, label, error, errorAppearance, errorMessage, newErrorMessage, indeterminate, onChange, name, suffix, className, 'aria-describedby': ariaDescribedByProp, 'aria-label': ariaLabel, required, showAsterisk = true, verticalAlignment = CheckboxVerticalAlignment.Center, } = this.props;
        const { mobile: isMobile } = this.context;
        const focusedBox = !isMobile && this.state.focused && theme === CheckboxTheme.Box;
        const focusedIcon = !isMobile && this.state.focused && theme === CheckboxTheme.Default;
        const iconContent = this._renderIcon(focusedIcon);
        const shouldShowErrorIcon = error && errorMessage && !newErrorMessage;
        const Wrapper = newErrorMessage ? ErrorMessageWrapper : React.Fragment;
        const box = theme === 'box';
        const stStates = {
            error,
            newErrorMessage,
            box,
            disabled,
            checked: !!checked,
        };
        const isErrorMessageProvided = errorMessage && errorMessage.length > 0;
        const renderRequiredAsterisk = required && showAsterisk;
        const originalClassNameValue = st(classes.root, { disabled, error, newErrorMessage, checked: !!checked, box }, className, focusedBox ? classes.focused : '');
        const newErrorMessageClassNameValue = classes.container;
        const containerClassNameValue = !newErrorMessage
            ? originalClassNameValue
            : newErrorMessageClassNameValue;
        const ariaDescribedBy = getAriaDescribedBy([
            ariaDescribedByProp,
            errorMessage ? this._errorMessageId : '',
        ]);
        return (React.createElement(Wrapper, { ...(newErrorMessage && {
                visible: error && isErrorMessageProvided,
                message: errorMessage,
                messageId: this._errorMessageId,
                className: st(classes.root, stStates, className),
                'data-hook': this.props['data-hook'],
                errorAppearance,
            }) },
            React.createElement("div", { className: containerClassNameValue, "data-hook": this.props['data-hook'], ref: this._rootRef, onFocus: this._onRootFocus, onKeyDown: this._onRootKeyDown, ...this.getDataAttributes() },
                React.createElement(CoreCheckbox, { className: st(classes.core, { verticalAlignment }), checkedIcon: iconContent, uncheckedIcon: iconContent, indeterminateIcon: iconContent, indeterminate: indeterminate, checked: checked, onChange: onChange, name: name, disabled: disabled, ref: this.inputRef, onFocusByKeyboard: this._onFocus, onBlur: this._onBlur, "data-hook": CHECKBOX_DATA_HOOKS.CheckboxCore, "aria-invalid": error, "aria-describedby": ariaDescribedBy, "aria-label": ariaLabel, id: this._checkboxId, required: required },
                    React.createElement("div", { "data-hook": CHECKBOX_DATA_HOOKS.LabelWrapper, className: classnames(classes.label, {
                            [classes.suffixed]: !!suffix,
                        }) },
                        label,
                        renderRequiredAsterisk && React.createElement(Asterisk, null)),
                    suffix && (React.createElement("div", { className: `${classes.label} ${classes.suffix}` }, suffix)),
                    shouldShowErrorIcon ? this._getErrorSuffix() : null))));
    }
}
Checkbox.displayName = 'Checkbox';
Checkbox.defaultProps = {
    checked: false,
    disabled: false,
    label: '',
    error: false,
    errorMessage: '',
    indeterminate: false,
    theme: CheckboxTheme.Default,
    'data-hook': CHECKBOX_DATA_HOOKS.CheckboxWrapper,
};
Checkbox.contextType = TPAComponentsContext;
//# sourceMappingURL=Checkbox.js.map